/**
 * AdminLeadsPage - Cross-account leads view for platform admins
 * Shows leads from all accounts with filtering and stats
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { formatPrice, LEAD_SOURCE_LABELS, cn } from '@limo/shared'
import type { LeadStatus, LeadSource } from '@limo/shared'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  TrendingUp,
  Clock,
  Search,
  Eye,
  Download,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  Building2,
  Flame,
  ArrowUpDown,
  Mail,
  ExternalLink,
  Archive,
  Trophy,
  ThumbsDown,
  Target,
  PhoneCall,
  CalendarClock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react'
import { LeadScoreBadge } from '@/components/leads/LeadScoreBadge'
import { LeadStageBadge } from '@/components/leads/LeadStageBadge'
import { LeadStatusBadge } from '@/components/leads/LeadStatusBadge'
import { LeadAgeIndicator } from '@/components/leads/LeadAgeIndicator'
import { downloadCsv, rowsToCsv } from '@/lib/reporting/csv'

// Helper to format relative time
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// Helper to format response time
function formatResponseTime(minutes: number | null | undefined): string {
  if (minutes == null) return '-'
  if (minutes < 60) return `${minutes}m`
  if (minutes < 1440) return `${Math.round(minutes / 60)}h`
  return `${Math.round(minutes / 1440)}d`
}

// Helper to format date/time
function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

interface AdminLead {
  id: string
  accountId: string
  accountName: string
  accountSlug: string
  customerName: string
  customerEmail: string | null
  customerPhone: string | null
  stageName: string
  stageColor: string
  source: LeadSource
  status: LeadStatus
  score: number
  scoreBreakdown: Record<string, number> | null
  estimatedValue: number | null
  nextFollowUpAt: string | null
  createdAt: string
  firstContactedAt: string | null
}

interface AdminLeadStats {
  totalLeads: number
  openLeads: number
  wonLeads: number
  lostLeads: number
  conversionRate: number
  followUpsDue: number
  awaitingContact: number
  avgResponseMinutes: number | null
  newToday: number
  hotLeads: number
  leadsByAccount: { accountId: string; accountName: string; count: number; avgResponse: number | null }[]
}

const PAGE_SIZES = [25, 50, 100]

export function AdminLeadsPage() {
  const navigate = useNavigate()
  const { startImpersonation } = useImpersonation()

  // Filter state
  const [search, setSearch] = useState('')
  const [accountFilter, setAccountFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [stageFilter, setStageFilter] = useState<string>('New Lead')

  // Pagination state
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Sorting state
  const [sortField, setSortField] = useState<string>('created_at')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Main tab state (active vs archive vs follow-ups)
  const [mainTab, setMainTab] = useState<'active' | 'archive' | 'follow-ups'>('active')

  // Archive state
  const [archiveOutcome, setArchiveOutcome] = useState<'all' | 'won' | 'lost'>('all')
  const [archivePage, setArchivePage] = useState(1)

  // Follow-ups tab state
  const [followUpFilter, setFollowUpFilter] = useState<'all' | 'overdue' | 'today' | 'upcoming'>('all')
  const [followUpPage, setFollowUpPage] = useState(1)

  // Sort handler
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
    setPage(1)
  }

  // Fetch all accounts for filter dropdown
  const { data: accounts } = useQuery({
    queryKey: ['admin', 'accounts-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name, slug')
        .eq('status', 'active')
        .order('name')

      if (error) throw error
      return data
    },
  })

  // Fetch cross-account stats
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['admin', 'lead-stats'],
    queryFn: async (): Promise<AdminLeadStats> => {
      // Get total and status counts with speed metrics
      const { data: statusData, error: statusError } = await supabase
        .from('leads')
        .select('status, account_id, first_contacted_at, created_at, score, accounts!inner(name)')

      if (statusError) throw statusError

      const statusCounts = { open: 0, won: 0, lost: 0, archived: 0 }
      const accountCounts: Record<string, { name: string; count: number; responseTimes: number[] }> = {}
      let awaitingContact = 0
      let newToday = 0
      let hotLeads = 0
      const responseTimes: number[] = []
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000)
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)

      statusData?.forEach((lead: any) => {
        statusCounts[lead.status as LeadStatus]++

        // New today
        if (new Date(lead.created_at) >= todayStart) {
          newToday++
        }

        // Hot leads (score >= 70, open)
        if (lead.status === 'open' && lead.score >= 70) {
          hotLeads++
        }

        // Track accounts
        if (!accountCounts[lead.account_id]) {
          accountCounts[lead.account_id] = { name: lead.accounts?.name || 'Unknown', count: 0, responseTimes: [] }
        }
        accountCounts[lead.account_id].count++

        // Track speed metrics
        if (lead.first_contacted_at) {
          const responseTime = (new Date(lead.first_contacted_at).getTime() - new Date(lead.created_at).getTime()) / 60000
          responseTimes.push(responseTime)
          accountCounts[lead.account_id].responseTimes.push(responseTime)
        } else if (lead.status === 'open' && new Date(lead.created_at) < thirtyMinutesAgo) {
          awaitingContact++
        }
      })

      // Get follow-ups due
      const { count: followUpsDue } = await supabase
        .from('lead_follow_ups')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending')
        .lte('due_at', new Date().toISOString())

      const total = statusData?.length || 0
      const wonPlusLost = statusCounts.won + statusCounts.lost
      const conversionRate = wonPlusLost > 0 ? (statusCounts.won / wonPlusLost) * 100 : 0
      const avgResponseMinutes = responseTimes.length > 0
        ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length)
        : null

      return {
        totalLeads: total,
        openLeads: statusCounts.open,
        wonLeads: statusCounts.won,
        lostLeads: statusCounts.lost,
        conversionRate: Math.round(conversionRate * 10) / 10,
        followUpsDue: followUpsDue || 0,
        awaitingContact,
        avgResponseMinutes,
        newToday,
        hotLeads,
        leadsByAccount: Object.entries(accountCounts)
          .map(([accountId, data]) => ({
            accountId,
            accountName: data.name,
            count: data.count,
            avgResponse: data.responseTimes.length > 0
              ? Math.round(data.responseTimes.reduce((a, b) => a + b, 0) / data.responseTimes.length)
              : null,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 5),
      }
    },
    staleTime: 30 * 1000,
  })

  // Fetch distinct stage names for the filter dropdown
  const { data: stageNames } = useQuery({
    queryKey: ['admin', 'stage-names'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lead_stages')
        .select('name, color')
        .order('position', { ascending: true })

      if (error) throw error

      // Deduplicate by name (stages are per-account but share names)
      const seen = new Set<string>()
      const unique: { name: string; color: string }[] = []
      for (const stage of data || []) {
        if (!seen.has(stage.name)) {
          seen.add(stage.name)
          unique.push({ name: stage.name, color: stage.color })
        }
      }
      return unique
    },
    staleTime: 5 * 60 * 1000,
  })

  // Fetch leads with filters
  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['admin', 'leads', search, accountFilter, sourceFilter, stageFilter, page, pageSize, sortField, sortDirection],
    queryFn: async () => {
      let query = supabase
        .from('leads')
        .select(`
          id,
          account_id,
          customer_name,
          customer_email,
          customer_phone,
          source,
          status,
          score,
          score_breakdown,
          estimated_value,
          next_follow_up_at,
          created_at,
          first_contacted_at,
          accounts!inner (
            id,
            name,
            slug
          ),
          lead_stages!inner (
            name,
            color
          )
        `, { count: 'exact' })
        .order(sortField, { ascending: sortDirection === 'asc' })

      // Exclude won/lost from active list (they appear in archive tab)
      query = query.not('status', 'in', '(won,lost)')

      // Apply filters
      if (search) {
        const searchTerm = `%${search}%`
        query = query.or(`customer_name.ilike.${searchTerm},customer_email.ilike.${searchTerm},customer_phone.ilike.${searchTerm}`)
      }
      if (accountFilter !== 'all') {
        query = query.eq('account_id', accountFilter)
      }
      if (sourceFilter !== 'all') {
        query = query.eq('source', sourceFilter)
      }
      if (stageFilter !== 'all') {
        query = query.eq('lead_stages.name', stageFilter)
      }

      // Pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      const leads: AdminLead[] = (data || []).map((lead: any) => ({
        id: lead.id,
        accountId: lead.account_id,
        accountName: lead.accounts?.name || 'Unknown',
        accountSlug: lead.accounts?.slug || '',
        customerName: lead.customer_name,
        customerEmail: lead.customer_email,
        customerPhone: lead.customer_phone,
        stageName: lead.lead_stages?.name || 'Unknown',
        stageColor: lead.lead_stages?.color || '#6b7280',
        source: lead.source,
        status: lead.status,
        score: lead.score,
        scoreBreakdown: lead.score_breakdown,
        estimatedValue: lead.estimated_value,
        nextFollowUpAt: lead.next_follow_up_at,
        createdAt: lead.created_at,
        firstContactedAt: lead.first_contacted_at,
      }))

      return {
        leads,
        total: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
      }
    },
    staleTime: 15 * 1000,
  })

  // Fetch archived leads (won + lost) across all accounts
  const { data: archiveData, isLoading: archiveLoading } = useQuery({
    queryKey: ['admin', 'archived-leads', archiveOutcome, archivePage, pageSize, accountFilter],
    queryFn: async () => {
      let query = supabase
        .from('leads')
        .select(`
          id,
          account_id,
          customer_name,
          customer_email,
          customer_phone,
          source,
          status,
          score,
          estimated_value,
          lost_reason,
          outcome_notes,
          created_at,
          updated_at,
          accounts!inner (id, name, slug),
          lead_stages!inner (name, color)
        `, { count: 'exact' })
        .order('updated_at', { ascending: false })

      // Filter by outcome
      if (archiveOutcome === 'won') {
        query = query.eq('status', 'won')
      } else if (archiveOutcome === 'lost') {
        query = query.eq('status', 'lost')
      } else {
        query = query.in('status', ['won', 'lost'])
      }

      // Apply account filter if set
      if (accountFilter !== 'all') {
        query = query.eq('account_id', accountFilter)
      }

      // Pagination
      const from = (archivePage - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query
      if (error) throw error

      // Get counts for sub-tabs
      let wonQuery = supabase.from('leads').select('*', { count: 'exact', head: true }).eq('status', 'won')
      let lostQuery = supabase.from('leads').select('*', { count: 'exact', head: true }).eq('status', 'lost')

      if (accountFilter !== 'all') {
        wonQuery = wonQuery.eq('account_id', accountFilter)
        lostQuery = lostQuery.eq('account_id', accountFilter)
      }

      const [{ count: wonCount }, { count: lostCount }] = await Promise.all([wonQuery, lostQuery])

      const leads = (data || []).map((lead: any) => ({
        id: lead.id,
        accountId: lead.account_id,
        accountName: lead.accounts?.name || 'Unknown',
        accountSlug: lead.accounts?.slug || '',
        customerName: lead.customer_name,
        customerEmail: lead.customer_email,
        customerPhone: lead.customer_phone,
        stageName: lead.lead_stages?.name || 'Unknown',
        stageColor: lead.lead_stages?.color || '#6b7280',
        source: lead.source,
        status: lead.status as LeadStatus,
        score: lead.score,
        estimatedValue: lead.estimated_value,
        lostReason: lead.lost_reason,
        outcomeNotes: lead.outcome_notes,
        createdAt: lead.created_at,
        updatedAt: lead.updated_at,
      }))

      return {
        leads,
        total: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        wonCount: wonCount || 0,
        lostCount: lostCount || 0,
      }
    },
    staleTime: 15 * 1000,
  })

  // Fetch follow-ups across all accounts
  const { data: followUpsData, isLoading: followUpsLoading } = useQuery({
    queryKey: ['admin', 'follow-ups', followUpFilter, followUpPage, pageSize, accountFilter],
    queryFn: async () => {
      const now = new Date()
      const todayStart = new Date(now)
      todayStart.setHours(0, 0, 0, 0)
      const todayEnd = new Date(now)
      todayEnd.setHours(23, 59, 59, 999)

      let query = supabase
        .from('lead_follow_ups')
        .select(`
          id,
          lead_id,
          title,
          description,
          due_at,
          status,
          assigned_to,
          leads!inner (
            id,
            account_id,
            customer_name,
            customer_email,
            customer_phone,
            score,
            status,
            accounts!inner (id, name, slug),
            lead_stages!inner (name, color)
          )
        `, { count: 'exact' })
        .eq('status', 'pending')
        .in('leads.status', ['open'])

      // Apply account filter
      if (accountFilter !== 'all') {
        query = query.eq('leads.account_id', accountFilter)
      }

      // Apply time filter
      if (followUpFilter === 'overdue') {
        query = query.lt('due_at', now.toISOString())
      } else if (followUpFilter === 'today') {
        query = query.gte('due_at', todayStart.toISOString()).lte('due_at', todayEnd.toISOString())
      } else if (followUpFilter === 'upcoming') {
        query = query.gt('due_at', todayEnd.toISOString())
      }

      query = query.order('due_at', { ascending: true })

      const from = (followUpPage - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query
      if (error) throw error

      // Get counts for sub-filter badges
      const buildCountQuery = (timeFilter?: 'overdue' | 'today' | 'upcoming') => {
        let q = supabase
          .from('lead_follow_ups')
          .select('*, leads!inner(account_id, status)', { count: 'exact', head: true })
          .eq('status', 'pending')
          .in('leads.status', ['open'])

        if (accountFilter !== 'all') {
          q = q.eq('leads.account_id', accountFilter)
        }
        if (timeFilter === 'overdue') q = q.lt('due_at', now.toISOString())
        else if (timeFilter === 'today') q = q.gte('due_at', todayStart.toISOString()).lte('due_at', todayEnd.toISOString())
        else if (timeFilter === 'upcoming') q = q.gt('due_at', todayEnd.toISOString())

        return q
      }

      const [
        { count: overdueCount },
        { count: todayCount },
        { count: upcomingCount },
        { count: totalPending },
      ] = await Promise.all([
        buildCountQuery('overdue'),
        buildCountQuery('today'),
        buildCountQuery('upcoming'),
        buildCountQuery(),
      ])

      const followUps = (data || []).map((item: any) => ({
        id: item.id,
        leadId: item.lead_id,
        title: item.title,
        description: item.description,
        dueAt: item.due_at,
        status: item.status,
        assignedTo: item.assigned_to,
        leadName: item.leads?.customer_name || 'Unknown',
        leadEmail: item.leads?.customer_email,
        leadPhone: item.leads?.customer_phone,
        leadScore: item.leads?.score || 0,
        leadStageName: item.leads?.lead_stages?.name || 'Unknown',
        leadStageColor: item.leads?.lead_stages?.color || '#6b7280',
        leadStatus: item.leads?.status || 'open',
        accountName: item.leads?.accounts?.name || 'Unknown',
        accountSlug: item.leads?.accounts?.slug || '',
        accountId: item.leads?.account_id || '',
      }))

      return {
        followUps,
        total: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize),
        overdueCount: overdueCount || 0,
        todayCount: todayCount || 0,
        upcomingCount: upcomingCount || 0,
        totalPending: totalPending || 0,
      }
    },
    staleTime: 15 * 1000,
  })

  // Mutation to complete a follow-up
  const queryClient = useQueryClient()
  const completeFollowUp = useMutation({
    mutationFn: async ({ followUpId }: { followUpId: string }) => {
      const { data, error } = await supabase
        .from('lead_follow_ups')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
        })
        .eq('id', followUpId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'follow-ups'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'lead-stats'] })
    },
  })

  // Handle view as account (starts impersonation)
  const handleViewAsAccount = (lead: AdminLead) => {
    startImpersonation(lead.accountSlug)
    navigate(`/admin/accounts/${lead.accountSlug}/view/leads/${lead.id}`)
  }

  // Handle export CSV
  const handleExportCSV = async () => {
    // Build query without pagination
    let query = supabase
      .from('leads')
      .select(`
        id,
        customer_name,
        customer_email,
        customer_phone,
        source,
        status,
        score,
        estimated_value,
        created_at,
        accounts!inner (name),
        lead_stages!inner (name)
      `)
      .order('created_at', { ascending: false })

    if (accountFilter !== 'all') {
      query = query.eq('account_id', accountFilter)
    }
    if (sourceFilter !== 'all') {
      query = query.eq('source', sourceFilter)
    }

    // Tab-aware filtering
    if (mainTab === 'active') {
      query = query.not('status', 'in', '(won,lost)')
    } else {
      if (archiveOutcome === 'won') {
        query = query.eq('status', 'won')
      } else if (archiveOutcome === 'lost') {
        query = query.eq('status', 'lost')
      } else {
        query = query.in('status', ['won', 'lost'])
      }
    }

    const { data, error } = await query

    if (error || !data) return

    // Build CSV
    const headers = ['Account', 'Name', 'Email', 'Phone', 'Stage', 'Source', 'Status', 'Score', 'Value', 'Created']
    const rows = data.map((lead: any) => [
      lead.accounts?.name || '',
      lead.customer_name || '',
      lead.customer_email || '',
      lead.customer_phone || '',
      lead.lead_stages?.name || '',
      lead.source || '',
      lead.status || '',
      lead.score || 0,
      lead.estimated_value || 0,
      new Date(lead.created_at).toLocaleDateString(),
    ])

    // Shared writer — see the note in LeadsPage.tsx.
    downloadCsv(
      `leads-${mainTab}-${new Date().toISOString().split('T')[0]}.csv`,
      rowsToCsv(headers, rows)
    )
  }

  // Calculate if there are active filters
  const hasFilters = search || accountFilter !== 'all' || sourceFilter !== 'all' || stageFilter !== 'New Lead'

  // Reset page when filters change
  const handleFilterChange = (setter: (value: string) => void, value: string) => {
    setter(value)
    setPage(1)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
          <p className="text-muted-foreground">
            View and manage leads across all accounts
          </p>
        </div>
        <Button onClick={handleExportCSV} variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Today</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats?.newToday || 0}</div>
            )}
            <p className="text-xs text-muted-foreground">across all accounts</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Awaiting Contact</CardTitle>
            <PhoneCall className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-amber-600">
                {stats?.awaitingContact || 0}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">
                {formatResponseTime(stats?.avgResponseMinutes)}
              </div>
            )}
            <p className="text-xs text-muted-foreground">to first contact</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hot Leads</CardTitle>
            <Flame className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-red-600">
                {stats?.hotLeads || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground">score 70+</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats?.conversionRate || 0}%
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs (Active / Archive / Follow Up) */}
      <Tabs value={mainTab} onValueChange={(v) => setMainTab(v as 'active' | 'archive' | 'follow-ups')}>
        <TabsList>
          <TabsTrigger value="active" className="gap-2">
            <Target className="h-4 w-4" />
            Active
            {stats && (
              <Badge variant="secondary" className="ml-1">
                {stats.openLeads}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="archive" className="gap-2">
            <Archive className="h-4 w-4" />
            Archive
            {stats && (
              <Badge variant="secondary" className="ml-1">
                {stats.wonLeads + stats.lostLeads}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="follow-ups" className="gap-2">
            <CalendarClock className="h-4 w-4" />
            Follow Up
            {followUpsData && followUpsData.totalPending > 0 && (
              <Badge
                variant="secondary"
                className={cn(
                  'ml-1',
                  followUpsData.overdueCount > 0 && 'bg-red-100 text-red-700'
                )}
              >
                {followUpsData.totalPending}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Active Leads Tab */}
        <TabsContent value="active" className="space-y-4 mt-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search leads..."
                value={search}
                onChange={(e) => handleFilterChange(setSearch, e.target.value)}
                className="pl-9"
              />
            </div>

            <Select value={accountFilter} onValueChange={(v) => handleFilterChange(setAccountFilter, v)}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sourceFilter} onValueChange={(v) => handleFilterChange(setSourceFilter, v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {Object.entries(LEAD_SOURCE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={stageFilter} onValueChange={(v) => handleFilterChange(setStageFilter, v)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All Stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {stageNames?.map((stage) => (
                  <SelectItem key={stage.name} value={stage.name}>
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: stage.color }}
                      />
                      {stage.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch('')
                  setAccountFilter('all')
                  setSourceFilter('all')
                  setStageFilter('New Lead')
                  setPage(1)
                }}
              >
                Clear filters
              </Button>
            )}
          </div>

          {/* Leads Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>Active Leads</CardTitle>
              <CardDescription>
                {leadsData?.total
                  ? `Showing ${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, leadsData.total)} of ${leadsData.total} leads`
                  : 'Loading leads...'}
              </CardDescription>
            </CardHeader>
        <CardContent className="px-0 sm:px-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-8 p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-8 p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('customer_name')}
                    >
                      Customer
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-8 p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('score')}
                    >
                      Score
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>
                    <Button
                      variant="ghost"
                      className="h-8 p-0 font-medium hover:bg-transparent"
                      onClick={() => handleSort('estimated_value')}
                    >
                      Value
                      <ArrowUpDown className="ml-2 h-4 w-4" />
                    </Button>
                  </TableHead>
                  <TableHead>Next Follow-up</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leadsLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                    </TableRow>
                  ))
                ) : leadsData?.leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center">
                      {hasFilters ? 'No leads match the current filters.' : 'No leads found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  leadsData?.leads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/admin/leads/${lead.id}`)}
                    >
                      <TableCell>
                        <LeadAgeIndicator
                          createdAt={lead.createdAt}
                          firstContactedAt={lead.firstContactedAt}
                          size="sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {lead.accountName}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{lead.customerName}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {lead.customerEmail && (
                                <span className="flex items-center gap-1 truncate">
                                  <Mail className="h-3 w-3 flex-shrink-0" />
                                  <span className="truncate">{lead.customerEmail}</span>
                                </span>
                              )}
                              {!lead.customerEmail && lead.customerPhone && (
                                <span className="truncate">{lead.customerPhone}</span>
                              )}
                              {!lead.customerEmail && !lead.customerPhone && (
                                <span>No contact</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {lead.customerPhone ? (
                          <a href={`tel:${lead.customerPhone}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                            {lead.customerPhone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <LeadStageBadge stage={{ name: lead.stageName, color: lead.stageColor }} />
                      </TableCell>
                      <TableCell>
                        <LeadScoreBadge score={lead.score} breakdown={lead.scoreBreakdown} showLabel={false} size="sm" />
                      </TableCell>
                      <TableCell className="font-medium">
                        {lead.estimatedValue ? formatPrice(lead.estimatedValue) : '-'}
                      </TableCell>
                      <TableCell>
                        {lead.nextFollowUpAt ? (
                          <span
                            className={
                              new Date(lead.nextFollowUpAt) < new Date()
                                ? 'text-red-600 font-medium'
                                : 'text-muted-foreground'
                            }
                          >
                            {formatDateTime(lead.nextFollowUpAt)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/admin/leads/${lead.id}`)
                              }}
                              className="cursor-pointer"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              View Lead
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation()
                                handleViewAsAccount(lead)
                              }}
                              className="cursor-pointer"
                            >
                              <ExternalLink className="mr-2 h-4 w-4" />
                              View as Account
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {leadsData && leadsData.totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-4 sm:px-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v))
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {leadsData.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= leadsData.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          </CardContent>
          </Card>
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archive" className="space-y-4 mt-4">
          {/* Archive Sub-tabs */}
          <div className="flex items-center gap-4">
            <Tabs value={archiveOutcome} onValueChange={(v) => {
              setArchiveOutcome(v as 'all' | 'won' | 'lost')
              setArchivePage(1)
            }}>
              <TabsList>
                <TabsTrigger value="all" className="gap-2">
                  All
                  <Badge variant="secondary">{(archiveData?.wonCount || 0) + (archiveData?.lostCount || 0)}</Badge>
                </TabsTrigger>
                <TabsTrigger value="won" className="gap-2">
                  <Trophy className="h-4 w-4 text-green-600" />
                  Won
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {archiveData?.wonCount || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="lost" className="gap-2">
                  <ThumbsDown className="h-4 w-4 text-gray-500" />
                  Lost
                  <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                    {archiveData?.lostCount || 0}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Account filter also applies to archive */}
            <Select value={accountFilter} onValueChange={(v) => {
              setAccountFilter(v)
              setArchivePage(1)
            }}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Conversion Summary Card */}
          {archiveData && (archiveData.wonCount > 0 || archiveData.lostCount > 0) && (
            <Card className="bg-muted/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-8">
                  <div>
                    <p className="text-sm text-muted-foreground">Win Rate</p>
                    <p className="text-2xl font-bold text-green-600">
                      {archiveData.wonCount + archiveData.lostCount > 0
                        ? Math.round((archiveData.wonCount / (archiveData.wonCount + archiveData.lostCount)) * 100)
                        : 0}%
                    </p>
                  </div>
                  <div className="h-12 w-px bg-border" />
                  <div>
                    <p className="text-sm text-muted-foreground">Total Won</p>
                    <p className="text-2xl font-bold">{archiveData.wonCount}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Lost</p>
                    <p className="text-2xl font-bold">{archiveData.lostCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Archive Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>
                {archiveOutcome === 'all' ? 'All Archived Leads' : archiveOutcome === 'won' ? 'Won Leads' : 'Lost Leads'}
              </CardTitle>
              <CardDescription>
                {archiveData?.total
                  ? `${archiveData.total} lead${archiveData.total === 1 ? '' : 's'} in archive`
                  : 'No archived leads yet'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Closed</TableHead>
                      <TableHead className="w-[70px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archiveLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 8 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : archiveData?.leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="h-24 text-center">
                          No archived leads yet. Leads marked as won or lost will appear here.
                        </TableCell>
                      </TableRow>
                    ) : (
                      archiveData?.leads.map((lead) => (
                        <TableRow
                          key={lead.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => navigate(`/admin/leads/${lead.id}`)}
                        >
                          <TableCell>
                            <Badge variant="outline" className="font-normal">
                              {lead.accountName}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{lead.customerName}</p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {lead.customerEmail && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" />
                                    {lead.customerEmail}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {lead.customerPhone ? (
                              <a href={`tel:${lead.customerPhone}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                                {lead.customerPhone}
                              </a>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <LeadStatusBadge status={lead.status} />
                          </TableCell>
                          <TableCell className="font-medium">
                            {lead.estimatedValue ? formatPrice(lead.estimatedValue) : '-'}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            {lead.status === 'lost' && lead.lostReason && (
                              <p className="text-sm text-muted-foreground truncate" title={lead.lostReason}>
                                {lead.lostReason}
                              </p>
                            )}
                            {lead.outcomeNotes && (
                              <p className="text-sm text-muted-foreground truncate" title={lead.outcomeNotes}>
                                {lead.outcomeNotes}
                              </p>
                            )}
                            {!lead.lostReason && !lead.outcomeNotes && (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDateTime(lead.updatedAt)}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Open menu</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigate(`/admin/leads/${lead.id}`)
                                  }}
                                  className="cursor-pointer"
                                >
                                  <Eye className="mr-2 h-4 w-4" />
                                  View Lead
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleViewAsAccount({
                                      ...lead,
                                      scoreBreakdown: null,
                                      nextFollowUpAt: null,
                                      firstContactedAt: null,
                                    } as AdminLead)
                                  }}
                                  className="cursor-pointer"
                                >
                                  <ExternalLink className="mr-2 h-4 w-4" />
                                  View as Account
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Archive Pagination */}
              {archiveData && archiveData.totalPages > 1 && (
                <div className="flex items-center justify-between border-t px-4 py-4 sm:px-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(v) => {
                        setPageSize(Number(v))
                        setArchivePage(1)
                      }}
                    >
                      <SelectTrigger className="w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZES.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Page {archivePage} of {archiveData.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setArchivePage(archivePage - 1)}
                      disabled={archivePage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setArchivePage(archivePage + 1)}
                      disabled={archivePage >= archiveData.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Follow Up Tab */}
        <TabsContent value="follow-ups" className="space-y-4 mt-4">
          {/* Follow-up Sub-filter tabs */}
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={followUpFilter} onValueChange={(v) => {
              setFollowUpFilter(v as 'all' | 'overdue' | 'today' | 'upcoming')
              setFollowUpPage(1)
            }}>
              <TabsList>
                <TabsTrigger value="all" className="gap-2">
                  All
                  <Badge variant="secondary">{followUpsData?.totalPending || 0}</Badge>
                </TabsTrigger>
                <TabsTrigger value="overdue" className="gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Overdue
                  <Badge variant="secondary" className={cn(
                    (followUpsData?.overdueCount || 0) > 0 && 'bg-red-100 text-red-700'
                  )}>
                    {followUpsData?.overdueCount || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="today" className="gap-2">
                  Today
                  <Badge variant="secondary" className={cn(
                    (followUpsData?.todayCount || 0) > 0 && 'bg-amber-100 text-amber-700'
                  )}>
                    {followUpsData?.todayCount || 0}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="gap-2">
                  Upcoming
                  <Badge variant="secondary">{followUpsData?.upcomingCount || 0}</Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Account filter also applies to follow-ups */}
            <Select value={accountFilter} onValueChange={(v) => {
              setAccountFilter(v)
              setFollowUpPage(1)
            }}>
              <SelectTrigger className="w-[200px]">
                <Building2 className="mr-2 h-4 w-4" />
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Follow-ups Table */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>Scheduled Follow-Ups</CardTitle>
              <CardDescription>
                {followUpsData?.total
                  ? `${followUpsData.total} follow-up${followUpsData.total === 1 ? '' : 's'} ${followUpFilter === 'all' ? 'pending' : followUpFilter}`
                  : 'No follow-ups scheduled'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 sm:px-6">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Follow-Up</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead className="w-[70px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {followUpsLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 7 }).map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : followUpsData?.followUps.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <CalendarClock className="h-8 w-8" />
                            <p>No follow-ups scheduled.</p>
                            <p className="text-sm">Schedule follow-ups from lead detail pages.</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      followUpsData?.followUps.map((followUp: any) => {
                        const dueDate = new Date(followUp.dueAt)
                        const now = new Date()
                        const isOverdue = dueDate < now
                        const isToday = dueDate.toDateString() === now.toDateString()

                        return (
                          <TableRow
                            key={followUp.id}
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/admin/leads/${followUp.leadId}`)}
                          >
                            <TableCell>
                              <div className={cn(
                                'flex items-center gap-2',
                                isOverdue && 'text-red-600 font-medium',
                                isToday && !isOverdue && 'text-amber-600 font-medium'
                              )}>
                                {isOverdue && (
                                  <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                                  </span>
                                )}
                                {formatDateTime(followUp.dueAt)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-normal">
                                {followUp.accountName}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{followUp.leadName}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {followUp.leadEmail && (
                                    <span className="flex items-center gap-1 truncate">
                                      <Mail className="h-3 w-3 flex-shrink-0" />
                                      <span className="truncate">{followUp.leadEmail}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {followUp.leadPhone ? (
                                <a href={`tel:${followUp.leadPhone}`} className="text-primary hover:underline text-sm" onClick={(e) => e.stopPropagation()}>
                                  {followUp.leadPhone}
                                </a>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="max-w-[200px]">
                                <p className="font-medium truncate">{followUp.title}</p>
                                {followUp.description && (
                                  <p className="text-xs text-muted-foreground truncate">{followUp.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <LeadStageBadge stage={{ name: followUp.leadStageName, color: followUp.leadStageColor }} />
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  completeFollowUp.mutate({ followUpId: followUp.id })
                                }}
                                disabled={completeFollowUp.isPending}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Follow-ups Pagination */}
              {followUpsData && followUpsData.totalPages > 1 && (
                <div className="flex items-center justify-between border-t px-4 py-4 sm:px-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows per page:</span>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(v) => {
                        setPageSize(Number(v))
                        setFollowUpPage(1)
                      }}
                    >
                      <SelectTrigger className="w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZES.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Page {followUpPage} of {followUpsData.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setFollowUpPage(followUpPage - 1)}
                      disabled={followUpPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setFollowUpPage(followUpPage + 1)}
                      disabled={followUpPage >= followUpsData.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Top Accounts Card */}
      {stats && stats.leadsByAccount.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Accounts by Leads</CardTitle>
            <CardDescription>Accounts with the most leads and their average response time</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats.leadsByAccount.map((account, index) => (
                <div key={account.accountId} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium">
                      {index + 1}
                    </span>
                    <span className="font-medium">{account.accountName}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {account.avgResponse != null && (
                      <span className="text-sm text-muted-foreground">
                        Avg: {formatResponseTime(account.avgResponse)}
                      </span>
                    )}
                    <Badge variant="secondary">{account.count} leads</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

